import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  HelpCircle,
  RefreshCw,
  Loader2,
  ArrowRight,
  Server,
  Cloud,
  Key,
  Radio,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Requirement {
  id: string;
  name: string;
  description: string;
  category: 'backend' | 'cf_worker' | 'keys_models' | 'edge';
  status: 'ok' | 'missing' | 'warning' | 'unknown';
  detail?: string;
  fixHint?: string;
}

interface RuntimeStatusData {
  requirements: Requirement[];
  summary: {
    total: number;
    ok: number;
    missing: number;
    warning: number;
    unknown: number;
  };
  checkedAt: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORY_META: Record<string, { label: string; icon: React.ElementType }> = {
  backend:    { label: 'Cloud Function (Backend)', icon: Server },
  cf_worker:  { label: 'CF Worker (edge.sharpai.org)',  icon: Cloud  },
  keys_models:{ label: 'Keys & Models',              icon: Key   },
  edge:       { label: 'Edge Connectivity',           icon: Radio  },
};

const CATEGORY_ORDER = ['backend', 'cf_worker', 'keys_models', 'edge'];

const FIX_ROUTES: Record<string, { label: string; path: string }> = {
  'api-keys': { label: 'Go to API Keys', path: '/api-keys' },
  settings:   { label: 'Go to Settings', path: '/settings' },
  models:     { label: 'Go to Models',   path: '/models'   },
  system:     { label: 'Go to System',   path: '/system'   },
};

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: Requirement['status'] }) {
  switch (status) {
    case 'ok':      return <CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" />;
    case 'missing': return <XCircle      size={18} className="text-red-500 shrink-0 mt-0.5" />;
    case 'warning': return <AlertTriangle size={18} className="text-yellow-500 shrink-0 mt-0.5" />;
    default:        return <HelpCircle   size={18} className="text-gray-400 shrink-0 mt-0.5" />;
  }
}

function StatusBadge({ status }: { status: Requirement['status'] }) {
  const styles: Record<string, string> = {
    ok:      'bg-green-50  text-green-700  ring-green-600/20',
    missing: 'bg-red-50    text-red-700    ring-red-600/20',
    warning: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
    unknown: 'bg-gray-50   text-gray-500   ring-gray-600/20',
  };
  const labels: Record<string, string> = {
    ok: 'OK', missing: 'Missing', warning: 'Warning', unknown: 'Unknown',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function SummaryBar({ summary }: { summary: RuntimeStatusData['summary'] }) {
  const pct = Math.round((summary.ok / summary.total) * 100);
  const color = pct === 100 ? 'text-green-600' : pct >= 60 ? 'text-yellow-600' : 'text-red-600';
  const barColor = pct === 100 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="bg-white shadow rounded-lg p-5 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex items-center gap-4 shrink-0">
        <div className="text-center">
          <div className={`text-4xl font-bold tabular-nums ${color}`}>{pct}%</div>
          <div className="text-xs text-gray-500 mt-0.5">healthy</div>
        </div>
        {/* mini bar */}
        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
        <span className="flex items-center gap-1.5 text-green-700">
          <CheckCircle2 size={14} /> {summary.ok} OK
        </span>
        {summary.missing > 0 && (
          <span className="flex items-center gap-1.5 text-red-700">
            <XCircle size={14} /> {summary.missing} Missing
          </span>
        )}
        {summary.warning > 0 && (
          <span className="flex items-center gap-1.5 text-yellow-700">
            <AlertTriangle size={14} /> {summary.warning} Warning
          </span>
        )}
        {summary.unknown > 0 && (
          <span className="flex items-center gap-1.5 text-gray-500">
            <HelpCircle size={14} /> {summary.unknown} Unknown
          </span>
        )}
      </div>
    </div>
  );
}

function RequirementRow({ req }: { req: Requirement }) {
  const fix = req.fixHint ? FIX_ROUTES[req.fixHint] : null;
  const isActionable = req.status !== 'ok' && fix;

  return (
    <li className="px-5 py-4 flex items-start gap-4">
      <StatusIcon status={req.status} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900">{req.name}</span>
          <StatusBadge status={req.status} />
        </div>
        <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">{req.description}</p>
        {req.detail && (
          <p className={`mt-1 text-xs font-mono break-all ${
            req.status === 'ok'      ? 'text-gray-400' :
            req.status === 'missing' ? 'text-red-600'  :
            req.status === 'warning' ? 'text-yellow-700' : 'text-gray-400'
          }`}>
            {req.detail}
          </p>
        )}
      </div>
      {isActionable && (
        <Link
          to={fix!.path}
          className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap"
        >
          {fix!.label} <ArrowRight size={12} />
        </Link>
      )}
    </li>
  );
}

function CategorySection({ category, reqs }: { category: string; reqs: Requirement[] }) {
  const meta = CATEGORY_META[category] ?? { label: category, icon: Server };
  const Icon = meta.icon;
  const allOk = reqs.every(r => r.status === 'ok');

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/60 flex items-center gap-2">
        <Icon size={14} className={allOk ? 'text-green-500' : 'text-gray-400'} />
        <h2 className="text-sm font-semibold text-gray-700">{meta.label}</h2>
        <span className="ml-auto text-xs text-gray-400">
          {reqs.filter(r => r.status === 'ok').length}/{reqs.length} OK
        </span>
      </div>
      <ul className="divide-y divide-gray-50">
        {reqs.map(req => <RequirementRow key={req.id} req={req} />)}
      </ul>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function RuntimeStatus() {
  const [data, setData]       = useState<RuntimeStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getRuntimeStatus();
      setData(result);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load runtime status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Group requirements by category, in defined order
  const grouped: [string, Requirement[]][] = CATEGORY_ORDER
    .map(cat => {
      const reqs = (data?.requirements ?? []).filter(r => r.category === cat);
      return [cat, reqs] as [string, Requirement[]];
    })
    .filter(([, reqs]) => reqs.length > 0);

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Runtime Status</h1>
          <p className="mt-1 text-sm text-gray-500">
            Live check of all configuration requirements for end-to-end functionality.
            Fix missing items to enable chat completions and key sync.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
        >
          {loading
            ? <Loader2 size={14} className="animate-spin" />
            : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Summary bar */}
      {data && <SummaryBar summary={data.summary} />}

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="space-y-4">
          {CATEGORY_ORDER.map(cat => (
            <div key={cat} className="bg-white shadow rounded-lg overflow-hidden animate-pulse">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                <div className="h-3 bg-gray-200 rounded w-32" />
              </div>
              <div className="divide-y divide-gray-50">
                {[0, 1, 2].map(i => (
                  <div key={i} className="px-5 py-4 flex items-start gap-4">
                    <div className="w-4 h-4 mt-0.5 rounded-full bg-gray-100 shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-gray-100 rounded w-40" />
                      <div className="h-2.5 bg-gray-50 rounded w-64" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Requirement groups */}
      {!loading && grouped.map(([cat, reqs]) => (
        <CategorySection key={cat} category={cat} reqs={reqs} />
      ))}

      {/* Footer */}
      {data && (
        <p className="text-xs text-gray-400 text-right">
          Checked at {new Date(data.checkedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}
